import React, { useMemo } from 'react';
import { Select, Card, Tag, Spin, Alert, Typography, Space } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { trpc as api } from '@/lib/api';
import type { DefaultOptionType } from 'antd/es/select';

const { Option } = Select;
const { Text } = Typography;

interface TemplateEquipment {
  equipmentId: string;
  equipmentName: string;
  isMandatory: boolean;
}

export interface WorkOrderTemplate {
  id: string;
  templateName: string;
  displayId: string;
  workTypeCategoryId?: string | null;
  workType?: 'inspection' | 'repair' | 'replacement' | 'maintenance' | null;
  workTypeCategory?: {
    id: string;
    categoryLabel?: string | null;
    color?: string | null;
    icon?: string | null;
  } | null;
  priority: string;
  standardHours: number;
  instructions?: string;
  safetyNotes?: string;
  equipments?: TemplateEquipment[];
}

interface TemplateSelectorProps {
  value?: WorkOrderTemplate | null;
  onChange: (value: WorkOrderTemplate | null) => void;
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  showEquipmentDetail?: boolean;
  showSearch?: boolean;
  onSelect?: (templateId: string) => void;
  onClear?: () => void;
}



const priorityLabels: Record<string, { label: string; color: string }> = {
  low: { label: '低', color: 'green' },
  medium: { label: '中', color: 'orange' },
  high: { label: '高', color: 'red' },
  urgent: { label: '緊急', color: 'red' }
};

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  allowClear = false,
  placeholder = '作業指示書を選択',
  showEquipmentDetail = true,
  showSearch = false,
  onSelect,
  onClear
}) => {
  // テンプレート一覧の取得
  const { data: templates, isLoading, error } = api.workOrderTemplate.list.useQuery({
    includeEquipments: true,
    includeInactive: false
  });
  const templateList = useMemo<WorkOrderTemplate[]>(
    () => (Array.isArray(templates) ? (templates as unknown as WorkOrderTemplate[]) : []),
    [templates]
  );

  // 選択値のIDを取得
  const { data: workTypeCategories = [] } = api.workTypeCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc'
  });

  const workTypeCategoryMap = useMemo(() => {
    const map = new Map<string, { categoryLabel?: string | null; color?: string | null }>();
    const list = Array.isArray(workTypeCategories) ? workTypeCategories : [];
    for (const category of list) {
      if (category?.id) {
        map.set(category.id, {
          categoryLabel: category.categoryLabel,
          color: category.color ?? undefined,
        });
      }
    }
    return map;
  }, [workTypeCategories]);

  const selectedId = value?.id;

  // 選択ハンドラー
  const handleSelect = (templateId: string | null) => {
    if (templateId === null || templateId === 'none') {
      onChange(null);
      onClear?.();
      return;
    }

    const selectedTemplate = templateList.find(t => t.id === templateId);
    if (selectedTemplate) {
      onChange(selectedTemplate);
      onSelect?.(templateId);
    }
  };

  // クリアハンドラー
  const handleClear = () => {
    onChange(null);
    onClear?.();
  };

  // フィルター関数（検索用）
  const filterOption = (input: string, option?: DefaultOptionType) => {
    if (!showSearch) {
      return true;
    }

    const optionValue = option?.value;
    if (typeof optionValue !== 'string') {
      return false;
    }

    const template = templateList.find(t => t.id === optionValue);
    if (!template) {
      return false;
    }

    const searchLower = input.toLowerCase();
    return (
      template.templateName.toLowerCase().includes(searchLower) ||
      template.displayId.toLowerCase().includes(searchLower)
    );
  };

  // ローディング状態
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          data-testid="loading-spinner"
        />
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <Alert
        message="テンプレートの取得に失敗しました"
        description={(error as { message?: string } | null)?.message ?? '不明なエラー'}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      <Select
        style={{ width: '100%' }}
        placeholder={placeholder}
        value={selectedId}
        onChange={handleSelect}
        disabled={disabled}
        allowClear={allowClear}
        showSearch={showSearch}
        filterOption={filterOption}
        onClear={handleClear}
        optionLabelProp="label"
      >
        {allowClear && (
          <Option value="none" label="なし（テンプレートを使用しない）">
            <Text type="secondary">なし（テンプレートを使用しない）</Text>
          </Option>
        )}
        {templateList.map(template => (
          <Option
            key={template.id}
            value={template.id}
            label={template.templateName}
          >
            <div>
              <Text strong>{template.templateName}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {template.displayId}
              </Text>
            </div>
          </Option>
        ))}
      </Select>

      {/* 選択されたテンプレートの詳細表示 */}
      {value && (
        <Card style={{ marginTop: 16 }} size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>{value.templateName}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {value.displayId}
              </Text>
            </div>

            <Space wrap>
              {(() => {
                const resolvedCategory =
                  value.workTypeCategory ??
                  (value.workTypeCategoryId
                    ? workTypeCategoryMap.get(value.workTypeCategoryId)
                    : undefined);
                const categoryLabel = resolvedCategory?.categoryLabel ?? '未設定';
                return (
                  <Tag color={resolvedCategory?.color ?? 'default'}>{categoryLabel}</Tag>
                );
              })()}
              <Text>
                優先度:{' '}
                {priorityLabels[value.priority] ? (
                  <Tag color={priorityLabels[value.priority].color}>
                    {priorityLabels[value.priority].label}
                  </Tag>
                ) : (
                  value.priority
                )}
              </Text>
              <Text>標準作業時間: {value.standardHours}時間</Text>
            </Space>

            {value.equipments && value.equipments.length > 0 && (
              <Text>関連設備: {value.equipments.length}台</Text>
            )}

            {/* 設備詳細 */}
            {showEquipmentDetail && value.equipments && value.equipments.length > 0 && (
              <div>
                <Text strong>関連設備:</Text>
                <div style={{ marginTop: 8 }}>
                  {value.equipments.map(eq => (
                    <Tag
                      key={eq.equipmentId}
                      color={eq.isMandatory ? 'red' : 'default'}
                      style={{ marginBottom: 4 }}
                    >
                      {eq.equipmentName}
                      <Text type="secondary" style={{ marginLeft: 4 }}>
                        {eq.isMandatory ? '（必須）' : '（任意）'}
                      </Text>
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

